// src/modules/app-views/landingPages/pages/Document/tabs/BuilderTab/LandingPageDataCard.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback } from 'react';
import { Card, Row, Col, Form, Input, Button, Tooltip, Space } from 'antd';
import { CopyOutlined, CheckOutlined, GlobalOutlined, RobotOutlined, ReloadOutlined } from '@ant-design/icons';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleSegment from 'components/SimpleComponents/SimpleSegment';
import translateX from 'utils/translateX';
import { generateUidV4 } from 'utils/helpers';

// UUID v4 regex for validation
const UUID_V4_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const LandingPageDataCard = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { form, locked } = useSimpleFormContext() || {};

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const pageType = Form.useWatch('pageType', form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // URL is always disabled when creating new (will be set automatically on publish)
  // When already published, follows viewMode via locked
  const isUrlDisabled = pageType === 'create' || locked;
  const isTokenDisabled = locked;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [tokenCopied, setTokenCopied] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleCopyToken = useCallback(() => {
    const currentToken = form?.getFieldValue('token') || '';
    if (!currentToken) return;
    navigator.clipboard.writeText(currentToken).then(() => {
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2000);
    });
  }, [form]);

  const handleGenerateToken = useCallback(() => {
    form?.setFieldValue('token', generateUidV4());
  }, [form]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Card title={<div className="text-muted">{translateX('landing_page_data')}</div>}>

      {/* Row 1: Name + page type toggle */}
      <Row gutter={ROW_GUTTER}>
        <Col xs={24} md={16}>
          <SimpleInput
            name="name"
            label="name"
            required
            useInitialInAvatar
            placeholder="landing_page"
          />
        </Col>

        <Col xs={24} md={8}>
          <SimpleSegment
            name="pageType"
            label="page_type"
            options={[
              {
                value: 'exists',
                label: translateX('lp_already_exists'),
                icon: <GlobalOutlined />,
              },
              {
                value: 'create',
                label: translateX('lp_create_new'),
                icon: <RobotOutlined />,
              },
            ]}
          />
        </Col>
      </Row>

      {/* Row 2: URL + Token */}
      <Row gutter={ROW_GUTTER}>
        <Col xs={24} md={16}>
          <SimpleInput
            name="url"
            label="url"
            placeholder="https://example.com"
            prefix={<GlobalOutlined />}
            disabled={isUrlDisabled}
            required={pageType === 'exists'}
            rules={[
              {
                validator: (_, value) => {
                  const isRequired = form?.getFieldValue('pageType') === 'exists';
                  if (isRequired && !value) {
                    return Promise.reject(new Error(translateX('required_field')));
                  }
                  if (value) {
                    try {
                      new URL(value);
                    } catch {
                      return Promise.reject(new Error(translateX('invalid_url')));
                    }
                  }
                  return Promise.resolve();
                },
              },
            ]}
          />
        </Col>

        <Col xs={24} md={8}>
          {/* Outer Form.Item for label/tooltip only — name is on the inner noStyle item */}
          <Form.Item
            label={translateX('token')}
            tooltip={translateX('token_paste_or_leave_blank')}
          >
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                name="token"
                noStyle
                rules={[
                  { required: true, message: () => translateX('required_field') },
                  {
                    validator: (_, value) => {
                      if (!value || UUID_V4_REGEX.test(value)) return Promise.resolve();
                      return Promise.reject(new Error(translateX('invalid_uuid')));
                    },
                  },
                ]}
              >
                <Input
                  disabled={isTokenDisabled}
                  placeholder={translateX('token_paste_or_leave_blank')}
                />
              </Form.Item>
              <Tooltip title={translateX('generate_new_token')}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleGenerateToken}
                  disabled={locked}
                />
              </Tooltip>
              <Tooltip title={tokenCopied ? translateX('copied') : translateX('copy')}>
                <Button
                  icon={tokenCopied ? <CheckOutlined /> : <CopyOutlined />}
                  onClick={handleCopyToken}
                />
              </Tooltip>
            </Space.Compact>
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default LandingPageDataCard;
